import { getDoc, runTransaction, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { patchPublicProfileCache } from '../data/publicProfileApi';
import {
  deckDoc,
  publicDirectoryDoc,
  publicTeamDirectoryDoc,
  teamDoc,
} from '../firebase/collections';
import type { Team } from '../features/teams/types';
import { canEditTeamProfile } from '../features/teams/utils/teamPermissions';
import type { Deck, DeckStatus, DeckVisibility } from '../types/deck';
import { normalizeMetafyArticle } from './metafyArticle';

export function isEligiblePlayerFeaturedDeck(deck: {
  visibility: DeckVisibility;
  status: DeckStatus;
  ownerId?: string;
}, ownerUid?: string): boolean {
  if (deck.visibility !== 'public' || deck.status !== 'published') return false;
  if (ownerUid && deck.ownerId && deck.ownerId !== ownerUid) return false;
  return true;
}

export function isEligibleTeamFeaturedDeck(
  deck: {
    visibility: DeckVisibility;
    status: DeckStatus;
    teamId?: string | null;
  },
  teamId: string
): boolean {
  if (deck.status !== 'published') return false;
  if (deck.teamId !== teamId) return false;
  return deck.visibility === 'team' || deck.visibility === 'public';
}

/** Whether a viewer may see a featured deck on a profile hero. */
export function canViewerSeeFeaturedDeck(
  deck: { visibility: DeckVisibility },
  opts: { isTeamMember?: boolean }
): boolean {
  if (deck.visibility === 'public') return true;
  if (deck.visibility === 'team') return Boolean(opts.isTeamMember);
  return false;
}

export async function setPlayerFeaturedDeck(opts: {
  slug: string;
  deckId: string | null;
}): Promise<void> {
  const slug = opts.slug.trim().toLowerCase();
  if (!slug) throw new Error('Missing player handle.');
  await updateDoc(publicDirectoryDoc(slug), {
    featuredDeckId: opts.deckId,
  });
  patchPublicProfileCache(slug, { featuredDeckId: opts.deckId });
}

export async function setTeamFeaturedDeck(opts: {
  teamId: string;
  deckId: string | null;
  callerUid: string;
}): Promise<void> {
  const now = Timestamp.now();
  const tRef = teamDoc(opts.teamId);

  await runTransaction(db, async (transaction) => {
    const teamSnap = await transaction.get(tRef);
    if (!teamSnap.exists()) throw new Error('Team not found.');
    const teamData = teamSnap.data() as Team;
    const role = teamData.members.find((m) => m.uid === opts.callerUid)?.role;
    if (!canEditTeamProfile(role)) {
      throw new Error('Only the owner or admins can feature a team deck.');
    }

    transaction.set(
      tRef,
      { featuredDeckId: opts.deckId, updatedAt: now },
      { merge: true }
    );
    transaction.set(
      publicTeamDirectoryDoc(teamData.slug),
      { featuredDeckId: opts.deckId, updatedAt: now },
      { merge: true }
    );
  });
}

export async function clearPlayerFeaturedDeckIfMatches(
  slug: string | null | undefined,
  deckId: string
): Promise<void> {
  const normalized = slug?.trim().toLowerCase();
  if (!normalized || !deckId) return;
  try {
    const snap = await getDoc(publicDirectoryDoc(normalized));
    if (!snap.exists()) return;
    if (snap.data()?.featuredDeckId !== deckId) return;
    await updateDoc(snap.ref, { featuredDeckId: null });
    patchPublicProfileCache(normalized, { featuredDeckId: null });
  } catch (err) {
    console.warn('[featuredDeck] clear player featured failed', err);
  }
}

export async function clearTeamFeaturedDeckIfMatches(
  teamId: string | null | undefined,
  deckId: string
): Promise<void> {
  if (!teamId || !deckId) return;
  try {
    const tRef = teamDoc(teamId);
    const snap = await getDoc(tRef);
    if (!snap.exists()) return;
    const teamData = snap.data() as Team;
    if (teamData.featuredDeckId !== deckId) return;
    const now = Timestamp.now();
    await runTransaction(db, async (transaction) => {
      transaction.set(tRef, { featuredDeckId: null, updatedAt: now }, { merge: true });
      transaction.set(
        publicTeamDirectoryDoc(teamData.slug),
        { featuredDeckId: null, updatedAt: now },
        { merge: true }
      );
    });
  } catch (err) {
    console.warn('[featuredDeck] clear team featured failed', err);
  }
}

/** Clear player + team featured pointers when a deck becomes ineligible or is deleted. */
export async function clearFeaturedPointersForDeck(opts: {
  deckId: string;
  ownerHandle?: string | null;
  teamId?: string | null;
  /** Previous visibility before a privacy change; used to decide which surfaces to clear. */
  nextVisibility?: DeckVisibility;
}): Promise<void> {
  const { deckId, ownerHandle, teamId, nextVisibility } = opts;
  const clearPlayer =
    nextVisibility === undefined || nextVisibility !== 'public';
  const clearTeam =
    nextVisibility === undefined ||
    (nextVisibility !== 'public' && nextVisibility !== 'team');

  await Promise.all([
    clearPlayer ? clearPlayerFeaturedDeckIfMatches(ownerHandle, deckId) : Promise.resolve(),
    clearTeam ? clearTeamFeaturedDeckIfMatches(teamId, deckId) : Promise.resolve(),
  ]);
}

export async function fetchTeamRoleForCaller(
  teamId: string,
  uid: string
): Promise<Team['members'][number]['role'] | undefined> {
  const snap = await getDoc(teamDoc(teamId));
  if (!snap.exists()) return undefined;
  const team = snap.data() as Team;
  return team.members.find((m) => m.uid === uid)?.role;
}

export async function fetchFeaturedDeckIdForPlayer(
  slug: string
): Promise<string | null> {
  const snap = await getDoc(publicDirectoryDoc(slug.trim().toLowerCase()));
  if (!snap.exists()) return null;
  const id = snap.data()?.featuredDeckId;
  return typeof id === 'string' && id.trim() ? id.trim() : null;
}

export async function fetchFeaturedDeckIdForTeam(
  teamId: string
): Promise<string | null> {
  const snap = await getDoc(teamDoc(teamId));
  if (!snap.exists()) return null;
  const id = (snap.data() as Team).featuredDeckId;
  return typeof id === 'string' && id.trim() ? id.trim() : null;
}

/** Resolve a deck by id when the caller can read it; returns null on miss/denied. */
export async function tryFetchDeckById(deckId: string): Promise<Deck | null> {
  if (!deckId.trim()) return null;
  try {
    const snap = await getDoc(deckDoc(deckId));
    if (!snap.exists()) return null;
    const data = snap.data() as Record<string, unknown>;
    // Inline minimal mapping to avoid circular imports with useDeck.
    return {
      id: snap.id,
      title: (data.title as string) || '',
      description: (data.description as string) || '',
      guide: (data.guide as string) || '',
      video: (data.video as Deck['video']) || undefined,
      metafy: normalizeMetafyArticle(data.metafy),
      playstyle: (data.playstyle as Deck['playstyle']) || [],
      formatCode: (data.formatCode as Deck['formatCode']) || 'UNL',
      tags: (data.tags as string[]) || [],
      coverCardId: (data.coverCardId as string) || undefined,
      matchups: (data.matchups as Deck['matchups']) || [],
      legend: (data.legend as Deck['legend']) || undefined,
      chosenLegends: (data.chosenLegends as Deck['chosenLegends']) || [],
      champion: (data.champion as Deck['champion']) || undefined,
      battlefields: (data.battlefields as Deck['battlefields']) || [],
      runes: (data.runes as Deck['runes']) || [],
      mainDeck: (data.mainDeck as Deck['mainDeck']) || [],
      sideboard: (data.sideboard as Deck['sideboard']) || [],
      considered: [],
      status: (data.status as Deck['status']) || 'draft',
      visibility: (data.visibility as Deck['visibility']) || 'public',
      ownerId: (data.ownerId as string) || '',
      ownerDisplayName: (data.ownerDisplayName as string) || '',
      ownerHandle: (data.ownerHandle as string) || '',
      teamId: typeof data.teamId === 'string' ? data.teamId : null,
      isValid: (data.isValid as boolean) ?? false,
      validationErrors: (data.validationErrors as string[]) || [],
      likeCount: (data.likeCount as number) || 0,
      viewCount: (data.viewCount as number) || 0,
      currentVersion: (data.currentVersion as number) || 0,
      createdAt: (data.createdAt as { toMillis?: () => number })?.toMillis?.() || Date.now(),
      updatedAt: (data.updatedAt as { toMillis?: () => number })?.toMillis?.() || Date.now(),
      tournamentResult: (data.tournamentResult as Deck['tournamentResult']) || undefined,
    };
  } catch {
    return null;
  }
}
